/**
 * Live alert heads-ups for the open mission (mounted once by the mission shell):
 *  - only alerts IMPORTANT FOR ME toast (takeoff, landing, broadcasts, the aircraft coming toward
 *    me / my camera / my site — @apos/shared alertFeed); info alerts go to the list only
 *  - bursts are coalesced (alertModel.coalesceAlerts) into one toast per situation
 *  - toasts fade out; PRIORITY stays until acknowledged
 *  - optional WebAudio chime; system notifications only while the tab is hidden
 * Wording is production-only: these are heads-ups for the content team, never safety instructions.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Alert, AlertViewer } from '@apos/shared';
import { buildAlertFeed, formatUtc } from '@apos/shared';
import { cx, Icon, IconButton } from '../../components/ui';
import { getNative } from '../../lib/native';
import { notifyAlert } from '../../lib/notifications';
import { usePrefs } from '../../lib/prefs';
import { onNewAlerts, useMission } from '../../state/mission';
import { useLiveUi } from '../live/liveUi';
import { BURST_WINDOW_MS, coalesceAlerts, SEVERITY_LABEL, toastLifetimeMs, type AlertGroup } from './alertModel';
import { useAlertPrefs } from './alertPrefs';
import { playChime } from './chime';
import { useAckAlerts } from './useAck';
import { useAlertViewer } from './useAlertFeed';
import './alerts.css';

interface ToastEntry {
  id: number;
  group: AlertGroup;
  expiresAt: number | null;
  leaving: boolean;
}

let nextId = 1;
const MAX_ENTRIES = 6;
const PHONE_TOAST_MS = 5000;

export function AlertLayer() {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const buffer = useRef<Alert[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ack = useAckAlerts();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const viewer = useAlertViewer();
  const viewerRef = useRef<AlertViewer | null>(viewer);
  viewerRef.current = viewer;

  // collect → coalesce → show
  useEffect(() => {
    const flush = () => {
      timer.current = null;
      const incoming = buffer.current;
      buffer.current = [];
      const { bundle, record } = useMission.getState();
      const v = viewerRef.current;
      if (!incoming.length || !bundle || !v) return;
      // classify against the whole history (one "coming toward you" heads-up per approach), keep the new ones
      const fresh = new Set(incoming.map((a) => a.id));
      const known = new Map((record?.alerts ?? []).map((a) => [a.id, a]));
      for (const a of incoming) known.set(a.id, a);
      const alerts = buildAlertFeed([...known.values()], bundle.alertRules, v)
        .filter((i) => fresh.has(i.alert.id) && i.forMe && i.level === 'important')
        .map((i) => ({ ...i.alert, title: i.title }));
      if (!alerts.length) return;
      const groups = coalesceAlerts(alerts, bundle.alertRules);
      const muted = usePrefs.getState().muteAlerts;
      const now = Date.now();
      const phone = typeof document !== 'undefined' && !!document.querySelector('.shell--phone');
      setItems((cur) => {
        // a newer group for the same situation replaces the old toast
        const keys = new Set(groups.map((g) => g.key));
        const kept = cur.filter((e) => !keys.has(e.group.key));
        const added = groups.map((g) => {
          // phones: the heads-up covers the map, so it clears quickly (history keeps it)
          const life = phone && g.severity !== 'warning' ? PHONE_TOAST_MS : toastLifetimeMs(g.severity);
          return { id: nextId++, group: g, expiresAt: life ? now + life : null, leaving: false };
        });
        return [...added, ...kept].slice(0, MAX_ENTRIES);
      });
      const top = groups[0]!;
      // Airview Studio app (build 8+): important heads-ups buzz, so a phone in the pocket still gets them
      if (!muted) getNative()?.haptic?.('important');
      if (!muted && top.severity !== 'info' && useAlertPrefs.getState().chime) playChime(top.severity);
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        for (const g of groups) {
          const lead = g.alerts[0]!;
          notifyAlert({ ...lead, id: `${g.key}@${lead.id}`, title: g.title, body: g.body, severity: g.severity }, bundle?.mission.name);
        }
      }
    };
    const off = onNewAlerts((alerts) => {
      buffer.current.push(...alerts);
      if (!timer.current) timer.current = setTimeout(flush, BURST_WINDOW_MS);
    });
    return () => {
      off();
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      buffer.current = [];
    };
  }, []);

  // expiry (fade out, then remove)
  useEffect(() => {
    if (!items.some((e) => e.expiresAt !== null)) return;
    const id = setInterval(() => {
      const now = Date.now();
      setItems((cur) => {
        let changed = false;
        const next = cur.flatMap((e) => {
          if (e.expiresAt === null) return [e];
          if (e.leaving && now >= e.expiresAt + 300) {
            changed = true;
            return [];
          }
          if (!e.leaving && now >= e.expiresAt) {
            changed = true;
            return [{ ...e, leaving: true }];
          }
          return [e];
        });
        return changed ? next : cur;
      });
    }, 250);
    return () => clearInterval(id);
  }, [items]);

  // drop toasts whose alerts were all acknowledged elsewhere (another device / the history drawer)
  const myId = useMission((s) => s.bundle?.me.userId);
  const alerts = useMission((s) => s.record?.alerts);
  useEffect(() => {
    if (!alerts || !myId) return;
    const acked = new Set(alerts.filter((a) => a.ackedBy.includes(myId)).map((a) => a.id));
    setItems((cur) => {
      const next = cur.filter((e) => !(e.group.severity === 'warning' && e.group.alerts.every((a) => acked.has(a.id))));
      return next.length === cur.length ? cur : next;
    });
  }, [alerts, myId]);

  const dismiss = (id: number) => setItems((cur) => cur.filter((e) => e.id !== id));
  const openHistory = () => {
    // phone: the alert list page (the bell's destination); desktop: the LIVE panel's Alerts tab
    if (document.querySelector('.shell--phone')) {
      nav('alerts');
      return;
    }
    useLiveUi.getState().openTab('alerts');
    if (!/\/live$/.test(location.pathname)) nav('live');
  };

  // reading the alert list already: no heads-up over it
  const onAlertList = /\/alerts$/.test(pathname);
  if (!items.length || onAlertList) return null;
  const visible = items.slice(0, 3);
  const hidden = items.length - visible.length;
  return (
    <div className="alert-toasts" aria-live="polite" data-testid="alert-toasts">
      {visible.map((e) => (
        <div
          key={e.id}
          className={cx('alert-toast', `alert-toast--${e.group.severity}`, e.leaving && 'is-leaving')}
          role={e.group.severity === 'warning' ? 'alert' : 'status'}
        >
          <div className="alert-toast__bar" aria-hidden />
          <button type="button" className="alert-toast__main" onClick={openHistory} title="Open alert history">
            <div className="alert-toast__meta">
              <span className="alert-toast__sev">{SEVERITY_LABEL[e.group.severity]}</span>
              <span className="num">{formatUtc(e.group.t)}</span>
              {e.group.alerts.length > 1 ? <span className="num">· {e.group.alerts.length} alerts</span> : null}
            </div>
            <div className="alert-toast__title">{e.group.title}</div>
            {e.group.body ? <div className="alert-toast__body">{e.group.body}</div> : null}
          </button>
          {e.group.severity === 'warning' ? (
            <button
              type="button"
              className="alert-toast__ack"
              onClick={() => {
                dismiss(e.id);
                void ack(e.group.alerts);
              }}
            >
              <Icon name="check" size={16} />
              ACK
            </button>
          ) : (
            <IconButton icon="close" label="Dismiss" size="sm" className="alert-toast__close" onClick={() => dismiss(e.id)} />
          )}
        </div>
      ))}
      {hidden > 0 ? (
        <button type="button" className="alert-toasts__more" onClick={openHistory}>
          +{hidden} more · open alerts
        </button>
      ) : null}
    </div>
  );
}
